// ============================================================
// Practice Material (AI practice) — shared types
// ============================================================

export type CourseRef = {
  course_id: string | number;
  course_name: string;
};

// Levels a practice can target (multiselect).
export const PRACTICE_LEVELS = ['Preparatory', 'Intermediate', 'Proficient', 'Advanced'] as const;
export type PracticeLevel = (typeof PRACTICE_LEVELS)[number];

// A Practice is a folder (Practice Studio). Course/level/type/description now
// live on the module, so they are optional here.
export type Practice = {
  id: string | number;
  name: string;
  folderId?: number;
  folderPath?: string;
  createdBy: string;
  createdByName?: string;
  moduleCount?: number;
  createdAt?: string;
  // legacy/optional display fields
  description?: string;
  courseIds?: (string | number)[];
  courseNames?: string[];
  levels?: string[];
  practiceType?: string;
};

// A module that belongs to a Practice.
export type PracticeModule = {
  id: string | number;
  name: string;
  description?: string;
  pitch?: string;
  tempo?: number | '';
  isEnabledForAI: boolean;
  moduleType: string;
  fileUrl?: string;
  // Module-level AI status returned by the folder resources API.
  aiVerified?: boolean;
  // Real pitch map from the backend (resource_list[].bars_metadata), when present.
  pitchMap?: PitchMap;
  // Source recording — used to (re)generate the pitch map via AudioML.
  recordingPath?: string;
  aiRecordingType?: string;
  pitchTempoOptions?: PitchTempoOption[];
};

export type PitchTempoOption = {
  pitchKey: string;
  tempo: number;
};

// One editable variant of a module (a resource: ai type × recording type).
export type ModuleVariant = {
  id: string | number; // resource id
  recordingType: string; // original | vocals_extracted
  aiRecordingType: string; // sing_along | practice | none
  pitchKey?: string;
  tempo?: number;
  transposedSemitones?: number | null;
  recordingPath?: string;
  isAiPracticeVerified?: boolean;
  pitchMap: PitchMap; // bars_metadata (bars_data may be empty)
};

export type CreatePracticePayload = {
  name: string;
  description: string;
  courseIds: (string | number)[];
  levels: string[];
  practiceType: string;
  createdBy: string;
  // Where to create the folder. Omit for a top-level category.
  parentFolderId?: number | null;
  folderPath?: string;
};

// Practice Module Type — given by product: Song / Alankar / KeyboardSingAlong
export const PRACTICE_MODULE_TYPES = ['Song', 'Alankar', 'KeyboardSingAlong'] as const;
export type PracticeModuleType = (typeof PRACTICE_MODULE_TYPES)[number];

// Practice Type — fixed dropdown, placeholder options (confirm real values later).
export const PRACTICE_TYPES = ['Warmup', 'Technique', 'Repertoire', 'Sight Reading', 'Theory'] as const;
export type PracticeType = (typeof PRACTICE_TYPES)[number];

// Pitch as a musical note (dropdown).
export const PITCH_NOTES = [
  'C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B',
] as const;
export type PitchNote = (typeof PITCH_NOTES)[number];

export type ModuleUploadForm = {
  name: string;
  description: string;
  pitch: PitchNote | '';
  tempo: number | '';
  isEnabledForAI: boolean;
  moduleType: PracticeModuleType | '';
};

// ---- AI pitch-map processing response (real algorithm output) ----
export type SangBy = 'teacher' | 'student';

// One detected pitch bar.
export type PitchBar = {
  id: number;
  starts: number; // seconds
  ends: number; // seconds
  avgs: number; // average frequency, Hz
  sangBy: SangBy;
  lyric: string; // romanized lyric / swar
  note: string; // western ("A3"), swar ("Sa"), or "" (derive from avgs)
  lyrics_hindi?: string;
  song_lyric_section_number?: number;
  song_lyric_word_number?: number;
  // Editor-only fine-tune, -10..+10 cents (not in raw algorithm output).
  cents?: number;
};

export type StudentSection = {
  section_number: number;
  starts: number;
  ends: number;
  duration: number;
  total_bars: number;
  bar_ids: number[];
  start_bar_id: number;
  end_bar_id: number;
  lyrics: string[];
  lyrics_hindi?: string[];
  notes: string[];
  sangBy: SangBy;
};

export type SectionWeights = {
  weighting_strategy: string;
  normalization: string;
  rules: {
    rule_type: string;
    section_numbers: number[];
    weight: number;
    label: string;
  }[];
};

// Full pitch map returned by the algorithm after a module upload.
export type PitchMap = {
  moduleId?: string | number;
  audioUrl?: string; // recording to play alongside the bars
  bars_data: PitchBar[];
  student_sections_data?: StudentSection[];
  student_section_weights?: SectionWeights;
};
